import React, { ButtonHTMLAttributes, forwardRef } from "react";
import { Loader2 } from "lucide-react";
import { analytics } from "../../utils/analytics";
import { useLocation } from "react-router-dom";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  fullWidth?: boolean;
  id?: string;
  trackingId?: string;
  page?: string;
  children: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      fullWidth = false,
      children,
      className = "",
      disabled,
      trackingId,
      page,
      onClick,
      ...props
    },
    ref
  ) => {
    const location = useLocation();

    // Auto-detect page context from current route if not provided
    const getPageContext = (): string => {
      if (page) return page;

      const pathname = location.pathname;

      // Map routes to page names
      if (pathname === "/" || pathname === "/app") return "app_start";
      if (pathname.includes("/contact")) return "contact_form";
      if (pathname.includes("/details")) return "details_form";
      if (pathname.includes("/upload") || pathname.includes("/file"))
        return "file_upload";
      if (pathname.includes("/price") || pathname.includes("/estimation"))
        return "price_estimation";
      if (pathname.includes("/booking") || pathname.includes("/calendar"))
        return "booking_calendar";
      if (pathname.includes("/confirmation")) return "confirmation";
      if (pathname.includes("/dashboard")) return "dashboard";
      if (pathname.includes("/admin")) return "admin_panel";

      // Fallback: extract page name from pathname
      const segments = pathname.split("/").filter(Boolean);
      return segments.length > 0 ? segments[segments.length - 1] : "app_start";
    };

    // Auto-generate button ID from children text if not provided
    const getButtonId = (): string => {
      if (trackingId) return trackingId;
      if (props.id) return props.id;
      if (props.name) return props.name;

      // Try to extract button text for ID
      if (typeof children === "string") {
        return children
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/[^a-z0-9_]/g, "");
      }

      // Fallback
      return "button_click";
    };
    const baseClasses =
      "inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

    const variantClasses = {
      primary: "bg-primary hover:bg-primary-600 text-white focus:ring-primary",
      secondary: "bg-gray-600 hover:bg-gray-700 text-white focus:ring-gray-500",
      outline:
        "border-2 border-primary text-primary hover:bg-primary-50 focus:ring-primary dark:text-primary-400 dark:hover:bg-primary-900/20",
      ghost:
        "text-gray-600 hover:bg-gray-100 focus:ring-gray-500 dark:text-gray-300 dark:hover:bg-gray-800",
      danger: "bg-red-500 hover:bg-red-600 text-white focus:ring-red-500",
    };

    const sizeClasses = {
      sm: "px-3 py-1.5 text-sm",
      md: "px-4 py-2 text-base",
      lg: "px-6 py-3 text-lg",
    };

    const widthClass = fullWidth ? "w-full" : "";

    const classes =
      `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClass} ${className}`.trim();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Track the button click with analytics
      const buttonId = getButtonId();
      const pageContext = getPageContext();
      analytics.trackButtonClick(buttonId, pageContext);

      // Call the original onClick handler if provided
      if (onClick) {
        onClick(e);
      }
    };

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        onClick={handleClick}
        {...props}
      >
        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
